import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Loader2,
  CheckCircle2,
  Tag,
  FileText,
  PartyPopper,
  Pencil,
  User,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { StepIndicator } from '@/components/ui/step-indicator';
import { ParagraphEditor } from '@/components/tags/ParagraphEditor';
import { TagSuggestionPanel } from '@/components/tags/TagSuggestionPanel';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { celebrationService } from '@/lib/api/celebration.service';
import { celebrationCelebrantService } from '@/lib/api/celebration-celebrant.service';
import { celebrationTagService } from '@/lib/api/celebrationTag.service';
import { celebrationParagraphService } from '@/lib/api/celebrationParagraph.service';
import { userContactService } from '@/lib/api/user-contact.service';
import { getChatLogForCelebration } from '@/lib/api/aiChatLog.service';
import type { Celebration } from '@/types/celebration.types';
import type { CelebrationCelebrant } from '@/types/user-contact.types';
import type { ParagraphStatus, TagsSnapshot, CelebrationParagraph, CelebrationTag } from '@/types/tag.types';

type SetupStep = 'paragraphs' | 'tags';
type PageMode = 'view' | 'edit';

const MIN_PARAGRAPH_WORDS = 50;

const hasMinimumParagraphWords = (value: string): boolean => {
  const wordCount = value.trim().split(/\s+/).filter(Boolean).length;
  return wordCount >= MIN_PARAGRAPH_WORDS;
};

export const CelebrationTagsSetup: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['celebrations', 'common']);

  const [celebration, setCelebration] = useState<Celebration | null>(null);
  const [celebrants, setCelebrants] = useState<CelebrationCelebrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit mode state
  const [mode, setMode] = useState<PageMode>('edit');
  const [step, setStep] = useState<SetupStep>('paragraphs');
  const [paragraphStatus, setParagraphStatus] = useState<ParagraphStatus | null>(null);
  const [tagsConfirmed, setTagsConfirmed] = useState(false);

  // View mode state
  const [paragraphs, setParagraphs] = useState<CelebrationParagraph[]>([]);
  const [currentTags, setCurrentTags] = useState<CelebrationTag[]>([]);
  const [prefillCelebrationText, setPrefillCelebrationText] = useState('');
  const [prefillCelebrantTexts, setPrefillCelebrantTexts] = useState<Record<number, string>>({});
  const [hasAppliedChatTags, setHasAppliedChatTags] = useState(false);

  const celebrationId = id ? parseInt(id) : null;

  const parseCelebrantNamesFromTitle = (title: string): string[] => {
    if (!title) return [];
    const cleaned = title
      .replace(/\b(wedding|birthday|anniversary|engagement|celebration|for|of)\b/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!cleaned) return [];

    return cleaned
      .split(/\s*(?:and|&)\s*|\s*,\s*/i)
      .map(name => name.trim())
      .filter(Boolean)
      .filter(name => name.length > 1)
      .slice(0, 4);
  };

  const isLikelyAutoGeneratedCelebrationParagraph = (content: string): boolean => {
    const normalized = content.trim().toLowerCase();
    return normalized.startsWith('this celebration, "')
      && normalized.includes('the overall goal is to create a joyful and memorable atmosphere');
  };

  const isLikelyAutoGeneratedCelebrantParagraph = (content: string): boolean => {
    const normalized = content.trim().toLowerCase();
    return normalized.includes('is being celebrated in this event as')
      && normalized.includes('this paragraph highlights their personality, values, and what makes this occasion special');
  };

  const tryAutofillSetup = async (
    celebData: Celebration,
    celebrantsData: CelebrationCelebrant[],
    existingParagraphs: CelebrationParagraph[]
  ): Promise<{ celebrants: CelebrationCelebrant[]; paragraphs: CelebrationParagraph[] }> => {
    let hydratedCelebrants = celebrantsData;
    let hydratedParagraphs = existingParagraphs;

    const existingCelebrationParagraph = hydratedParagraphs.find(
      paragraph => paragraph.paragraph_type === 'celebration'
    );
    const sourceDescription = typeof celebData.description === 'string' ? celebData.description.trim() : '';
    const canUseSourceDescription = hasMinimumParagraphWords(sourceDescription);

    if (existingCelebrationParagraph) {
      try {
        if (canUseSourceDescription && existingCelebrationParagraph.content.trim() !== sourceDescription) {
          await celebrationParagraphService.upsertParagraph(celebData.id, {
            paragraph_type: 'celebration',
            content: sourceDescription,
          });
          hydratedParagraphs = await celebrationParagraphService.getParagraphs(celebData.id).catch(() => hydratedParagraphs);
        } else if (!sourceDescription && isLikelyAutoGeneratedCelebrationParagraph(existingCelebrationParagraph.content)) {
          await celebrationParagraphService.deleteParagraph(celebData.id, existingCelebrationParagraph.id);
          hydratedParagraphs = await celebrationParagraphService.getParagraphs(celebData.id).catch(() => hydratedParagraphs);
        }
      } catch (error) {
        console.error('Setup autofill: failed to normalize celebration paragraph', error);
      }
    }

    const generatedCelebrantParagraphs = hydratedParagraphs.filter(
      paragraph => paragraph.paragraph_type === 'celebrant' && isLikelyAutoGeneratedCelebrantParagraph(paragraph.content)
    );

    if (generatedCelebrantParagraphs.length > 0) {
      try {
        for (const paragraph of generatedCelebrantParagraphs) {
          await celebrationParagraphService.deleteParagraph(celebData.id, paragraph.id);
        }
        hydratedParagraphs = await celebrationParagraphService.getParagraphs(celebData.id).catch(() => hydratedParagraphs);
      } catch (error) {
        console.error('Setup autofill: failed to remove generated celebrant paragraphs', error);
      }
    }

    // Backfill celebrants only if none exist
    if (hydratedCelebrants.length === 0) {
      const names = parseCelebrantNamesFromTitle(celebData.title || '');
      for (let index = 0; index < names.length; index++) {
        const name = names[index];
        try {
          console.warn(
            `[CelebrationTagsSetup] Backfilling celebrant "${name}" from title without an email. ` +
            `Update their email and role in the Celebrants section.`
          );
          const contact = await userContactService.createContact({
            full_name: name,
            relationship: 'Celebrant',
            status: 'new',
          });

          await celebrationCelebrantService.createCelebrant(celebData.id, {
            user_id: contact.user_id,
            role: 'Celebrant',
            is_surprise: false,
          });
        } catch (error) {
          console.error('Setup autofill: failed to create celebrant', { name, error });
        }
      }

      hydratedCelebrants = await celebrationCelebrantService.getCelebrants(celebData.id).catch(() => []);
    }

    // Backfill paragraphs only from user-provided source text (no auto-generation)
    if (hydratedParagraphs.length === 0) {
      if (canUseSourceDescription) {
        try {
          await celebrationParagraphService.upsertParagraph(celebData.id, {
            paragraph_type: 'celebration',
            content: sourceDescription,
          });
        } catch (error) {
          console.error('Setup autofill: failed to create celebration paragraph from source description', error);
        }
      }

      hydratedParagraphs = await celebrationParagraphService.getParagraphs(celebData.id).catch(() => []);
    }

    return { celebrants: hydratedCelebrants, paragraphs: hydratedParagraphs };
  };

  // Load celebration data and celebrants
  useEffect(() => {
    if (!celebrationId || isNaN(celebrationId)) {
      setError('Invalid celebration ID');
      setLoading(false);
      return;
    }
    loadData();
  }, [celebrationId]);

  const loadData = async () => {
    try {
      setLoading(true);
      let cachedChatLog: Awaited<ReturnType<typeof getChatLogForCelebration>> | undefined;
      const getChatLogOnce = async () => {
        if (cachedChatLog !== undefined) {
          return cachedChatLog;
        }

        cachedChatLog = await getChatLogForCelebration(celebrationId!);
        return cachedChatLog;
      };

      const [celebData, celebrantsData, tagsStatus] = await Promise.all([
        celebrationService.getCelebration(celebrationId!),
        celebrationCelebrantService.getCelebrants(celebrationId!),
        celebrationTagService.getTagsStatus(celebrationId!).catch(() => ({ tags_confirmed: false })),
      ]);

      setCelebration(celebData);
      setTagsConfirmed(tagsStatus.tags_confirmed);

      // Load paragraphs and tags for view mode
      const [initialParas, tags] = await Promise.all([
        celebrationParagraphService.getParagraphs(celebrationId!).catch(() => []),
        celebrationTagService.getTags(celebrationId!).catch(() => []),
      ]);

      if (tags.length === 0 && !hasAppliedChatTags) {
        try {
          const chatLog = await getChatLogOnce();
          if (chatLog?.confirmed_tags && Object.keys(chatLog.confirmed_tags).length > 0) {
            await celebrationTagService.assignTagsFromSnapshot(celebrationId!, chatLog.confirmed_tags, 'ai_accepted');
            await celebrationTagService.confirmTags(celebrationId!);
            setHasAppliedChatTags(true);
          }
        } catch (chatTagError) {
          console.error('Failed to copy chat tags:', chatTagError);
        }
      }

      if (initialParas.length === 0) {
        try {
          const chatLog = await getChatLogOnce();
          const firstUserMessage = chatLog?.conversation.find(entry => entry.role === 'user')?.content?.trim();
          if (firstUserMessage) {
            setPrefillCelebrationText(firstUserMessage);
          }
        } catch (chatError) {
          console.error('Failed to load chat log for prefill:', chatError);
        }
      }

      const hydrated = await tryAutofillSetup(celebData, celebrantsData, initialParas);

      setCelebrants(hydrated.celebrants);
      setParagraphs(hydrated.paragraphs);
      setCurrentTags(tags);

      if (hydrated.paragraphs.every(paragraph => paragraph.paragraph_type !== 'celebrant')) {
        try {
          const chatLog = await getChatLogOnce();
          const userMessages = (chatLog?.conversation ?? [])
            .filter(entry => entry.role === 'user')
            .map(entry => entry.content.trim())
            .filter(Boolean);

          const prefillMap: Record<number, string> = {};
          const celebrantNames = hydrated.celebrants
            .map(celebrant => (celebrant.user_contact?.full_name || celebrant.name || '').trim())
            .filter(Boolean);

          const scoreMessageForCelebrant = (message: string, targetName: string): number => {
            const lowerMessage = message.toLowerCase();
            const lowerTarget = targetName.toLowerCase();
            if (!lowerMessage.includes(lowerTarget)) return -1;

            let score = 2;
            for (const otherName of celebrantNames) {
              if (otherName === targetName) continue;
              if (lowerMessage.includes(otherName.toLowerCase())) {
                score -= 2;
              }
            }

            if (lowerMessage.includes('lives') || lowerMessage.includes('comes from') || lowerMessage.includes('from')) {
              score += 1;
            }

            if (message.length >= 40) {
              score += 1;
            }

            return score;
          };

          for (const celebrant of hydrated.celebrants) {
            const name = (celebrant.user_contact?.full_name || celebrant.name || '').trim();
            if (!name) continue;

            const ranked = userMessages
              .map(message => ({ message, score: scoreMessageForCelebrant(message, name) }))
              .filter(entry => entry.score >= 0)
              .sort((a, b) => b.score - a.score);

            if (ranked.length > 0) {
              prefillMap[celebrant.id] = ranked[0].message;
            }
          }

          if (Object.keys(prefillMap).length > 0) {
            setPrefillCelebrantTexts(prefillMap);
          }
        } catch (chatError) {
          console.error('Failed to load chat log for celebrant prefill:', chatError);
        }
      }

      // If tags are confirmed, show view mode
      if (tagsStatus.tags_confirmed) {
        setMode('view');
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e?.response?.data?.message || 'Failed to load celebration data');
    } finally {
      setLoading(false);
    }
  };

  const handleParagraphStatusChange = (status: ParagraphStatus) => {
    setParagraphStatus(status);
  };

  const handleTagsConfirmed = (_snapshot: TagsSnapshot) => {
    setTagsConfirmed(true);
    // Reload data for view mode
    loadData();
  };

  const handleTagsUnconfirmed = () => {
    setTagsConfirmed(false);
    setMode('edit');
    setStep('paragraphs');
  };

  const switchToEdit = () => {
    setMode('edit');
    setStep('paragraphs');
  };

  const canProceedToTags = paragraphStatus?.complete || false;

  const goToTags = () => {
    if (canProceedToTags) {
      setStep('tags');
    }
  };

  const goBack = () => {
    if (mode === 'edit' && step === 'tags') {
      setStep('paragraphs');
    } else {
      navigate(`/celebrations/${celebrationId}`);
    }
  };

  const goToCelebration = () => {
    navigate(`/celebrations/${celebrationId}`);
  };

  // Progress calculation
  const progressValue = step === 'paragraphs' ? (canProceedToTags ? 50 : 25) : tagsConfirmed ? 100 : 75;

  // Group tags by category for view mode
  const groupedTags: Record<string, string[]> = {};
  currentTags.forEach((t) => {
    if (!groupedTags[t.category_name]) groupedTags[t.category_name] = [];
    if (!groupedTags[t.category_name].includes(t.tag_value)) {
      groupedTags[t.category_name].push(t.tag_value);
    }
  });

  const draftParagraphs = paragraphs.length === 0
    ? [
        ...(prefillCelebrationText
          ? [{
              id: 'draft-celebration',
              paragraph_type: 'celebration' as const,
              content: prefillCelebrationText,
            }]
          : []),
        ...celebrants
          .map((celebrant) => ({
            id: `draft-celebrant-${celebrant.id}`,
            paragraph_type: 'celebrant' as const,
            celebrant_id: celebrant.id,
            celebrant_name: celebrant.user_contact?.full_name || celebrant.name || 'Celebrant',
            content: prefillCelebrantTexts[celebrant.id],
          }))
          .filter((draft) => !!draft.content),
      ]
    : [];

  if (loading) {
    return (
      <PageContainer width="default" className="space-y-6">
        <AppBreadcrumbs />
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground">{t('celebrations:tagsSetup.loading')}</span>
        </div>
      </PageContainer>
    );
  }

  if (error || !celebration) {
    return (
      <PageContainer width="default" className="space-y-6">
        <AppBreadcrumbs />
        <Card>
          <CardContent className="py-8 text-center text-destructive">
            {error || t('celebrations:tagsSetup.notFound')}
            <div className="mt-4">
              <Button variant="outline" onClick={() => navigate('/celebrations')}>
                <ArrowLeft className="h-4 w-4 mr-2" /> {t('celebrations:tagsSetup.backToCelebrations')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  // ── VIEW MODE ──────────────────────────────────────────────────────
  if (mode === 'view') {
    return (
      <PageContainer width="default" className="space-y-6">
        <AppBreadcrumbs />
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={goToCelebration}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                {t('celebrations:tagsSetup.title')}
              </h1>
              <p className="text-sm text-muted-foreground">{celebration.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={switchToEdit}>
              <Pencil className="h-3 w-3 mr-1" /> {t('celebrations:tagsSetup.edit')}
            </Button>
            <Button size="sm" onClick={goToCelebration}>
              <ArrowLeft className="h-3 w-3 mr-1" /> {t('celebrations:tagsSetup.backToCelebration')}
            </Button>
          </div>
        </div>

        {/* Status badge */}
        {tagsConfirmed && (
          <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
            <CheckCircle2 className="h-4 w-4" />
            {t('celebrations:tagsSetup.tagsConfirmed')}
          </div>
        )}

        {/* Paragraphs Card */}
        <Card>
          <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <span className="font-semibold text-sm">{t('celebrations:tagsSetup.paragraphsCard')}</span>
              <Badge variant="secondary" className="text-xs">{paragraphs.length}</Badge>
            </div>
          </div>
          <CardContent className="py-4">
            {paragraphs.length === 0 && draftParagraphs.length === 0 ? (
              <div className="text-center py-6">
                <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50" />
                <p className="text-sm text-muted-foreground">{t('celebrations:tagsSetup.noParagraphs')}</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={switchToEdit}>
                  <Pencil className="h-3 w-3 mr-1" /> {t('celebrations:tagsSetup.writeParagraphs')}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {[...paragraphs, ...draftParagraphs].map((p) => (
                  <div key={p.id} className="border rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      {p.paragraph_type === 'celebration' ? (
                        <PartyPopper className="h-3.5 w-3.5 text-primary" />
                      ) : (
                        <User className="h-3.5 w-3.5 text-primary" />
                      )}
                      <span className="text-sm font-medium">
                        {p.paragraph_type === 'celebration'
                          ? t('celebrations:tagsSetup.aboutCelebration')
                          : t('celebrations:tagsSetup.aboutCelebrant', { name: p.celebrant_name || 'Celebrant' })}
                      </span>
                      {'author_name' in p && p.author_name && (
                        <span className="text-xs text-muted-foreground ml-auto">
                          {t('celebrations:tagsSetup.by', { name: p.author_name })}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {p.content}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tags Card */}
        <Card>
          <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4 text-primary" />
              <span className="font-semibold text-sm">{t('celebrations:tagsSetup.tagsCard')}</span>
              <Badge variant="secondary" className="text-xs">{currentTags.length}</Badge>
            </div>
          </div>
          <CardContent className="py-4">
            {currentTags.length === 0 ? (
              <div className="text-center py-6">
                <Tag className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50" />
                <p className="text-sm text-muted-foreground">{t('celebrations:tagsSetup.noTags')}</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={switchToEdit}>
                  <Sparkles className="h-3 w-3 mr-1" /> {t('celebrations:tagsSetup.runAI')}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(groupedTags).map(([category, values]) => (
                  <div key={category} className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap mt-0.5 min-w-[80px]">
                      {category}:
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {values.map((value) => (
                        <Badge key={value} variant="secondary" className="text-xs">
                          {value}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  // ── EDIT MODE ──────────────────────────────────────────────────────
  return (
    <PageContainer width="default" className="space-y-6">
      <AppBreadcrumbs />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={goBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              {t('celebrations:tagsSetup.editTitle')}
            </h1>
            <p className="text-sm text-muted-foreground">{celebration.title}</p>
          </div>
        </div>
        {tagsConfirmed && (
          <Button onClick={goToCelebration} variant="default" size="sm">
            {t('celebrations:tagsSetup.continueButton')} <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        )}
      </div>

      {/* Progress stepper */}
      <div className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <StepIndicator
              number={1}
              label={t('celebrations:tagsSetup.writeParagraphs')}
              icon={<FileText className="h-3.5 w-3.5" />}
              active={step === 'paragraphs'}
              complete={canProceedToTags}
            />
            <div className="w-8 h-px bg-border" />
            <StepIndicator
              number={2}
              label={t('celebrations:tagsSetup.reviewTags')}
              icon={<Tag className="h-3.5 w-3.5" />}
              active={step === 'tags'}
              complete={tagsConfirmed}
            />
          </div>
        </div>
        <Progress value={progressValue} className="h-1.5" />
      </div>

      {/* Step content */}
      {step === 'paragraphs' && (
        <div className="space-y-4">
          <ParagraphEditor
            celebrationId={celebrationId!}
            celebrants={celebrants}
            onStatusChange={handleParagraphStatusChange}
            initialCelebrationText={prefillCelebrationText}
            initialCelebrantTexts={prefillCelebrantTexts}
          />

          {/* Next step button */}
          <div className="flex justify-end">
            <Button onClick={goToTags} disabled={!canProceedToTags} className="gap-2">
              {canProceedToTags ? (
                <>
                  {t('celebrations:tagsSetup.proceedAI')}
                  <Sparkles className="h-4 w-4" />
                </>
              ) : (
                <>
                  {t('celebrations:tagsSetup.completeParagraphs')}
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {step === 'tags' && (
        <div className="space-y-4">
          <TagSuggestionPanel
            celebrationId={celebrationId!}
            onTagsConfirmed={handleTagsConfirmed}
            onTagsUnconfirmed={handleTagsUnconfirmed}
          />

          {tagsConfirmed && (
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setStep('paragraphs')}>
                <ArrowLeft className="h-4 w-4 mr-1" /> {t('celebrations:tagsSetup.backToParagraphs')}
              </Button>
              <Button onClick={goToCelebration}>
                <PartyPopper className="h-4 w-4 mr-2" />
                {t('celebrations:tagsSetup.goToCelebration')}
              </Button>
            </div>
          )}
        </div>
      )}
    </PageContainer>
  );
};

// StepIndicator imported from @/components/ui/step-indicator
